import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import LanguageToggle from "./LanguageToggle";

export default function Navbar() {
  const { t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const LINKS = [
    { href: "/#home", label: t.nav.home },
    { href: "/#services", label: t.nav.services },
    { href: "/#how-it-works", label: t.nav.howItWorks },
    { href: "/#why-us", label: t.nav.whyUs },
    { href: "/blog", label: t.nav.blog },
    { href: "/#faq", label: t.nav.faq },
    { href: "/#quote", label: t.nav.quote },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-ink/80 border-b border-ink-line py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <a
          href="#home"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="/logos/truck-mark.jpg"
            alt="Superstate LLC"
            className={`rounded transition-all duration-300 ${
              scrolled ? "h-10 md:h-11" : "h-12 md:h-14"
            }`}
          />
          <span className="hidden sm:block font-heading text-2xl md:text-3xl text-gold tracking-wide leading-none">
            SUPERSTATE <span className="text-white">LLC</span>
          </span>
        </a>

        <nav className="hidden xl:flex items-center gap-5 2xl:gap-7 min-w-0 flex-wrap justify-end">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.replace(/[\/#]/g, "")}`}
              className="text-sm uppercase tracking-wider text-white/80 hover:text-gold transition-colors font-semibold"
            >
              {l.label}
            </a>
          ))}
          <LanguageToggle />
          <a
            href="tel:7865438906"
            data-testid="nav-call-btn"
            className="bg-gold text-ink font-bold uppercase text-sm tracking-wider px-4 py-2.5 rounded hover:bg-gold-hover transition-all hover:shadow-[0_0_20px_rgba(255,215,0,0.4)] whitespace-nowrap"
          >
            {t.nav.callBtn}
          </a>
        </nav>

        <div className="xl:hidden flex items-center gap-3">
          <LanguageToggle />
          <button
            data-testid="nav-mobile-toggle"
            className="text-white"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="xl:hidden bg-ink/95 backdrop-blur-xl border-t border-ink-line"
        >
          <div className="flex flex-col px-6 py-6 gap-4">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-white/80 hover:text-gold font-semibold uppercase tracking-widest text-sm"
              >
                {l.label}
              </a>
            ))}
            <a
              href="tel:7865438906"
              className="bg-gold text-ink font-bold uppercase text-sm tracking-wider px-5 py-3 rounded text-center"
            >
              {t.nav.callBtn}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
